import React, { useEffect, useMemo, useState } from "react";
import { Cascader } from "antd-mobile";
import { useCtx, useT } from "@al/next/al-ex";
import { Toast } from "@al/akui-mobile";

import Iconfont from "~/components/ui/Iconfont";
import redux from "~/global/redux/address";

import styles from "./index.scss";

export interface CascaderOptionItem {
  label: string;
  value: string;
}

export interface SelectAddressItem {
  provinceId?: string;
  cityId?: string;
  districtId?: string;
  villageId?: string;
  provinceName?: string;
  cityName?: string;
  districtName?: string;
  villageName?: string;
}

interface Props {
  addressList: CascaderOptionItem[];
  visible: boolean;
  onClose?: () => void;
  onSelectItem?: (item: SelectAddressItem) => void;
}

type CascaderOption = {
  label: string;
  value: string;
  disabled?: boolean;
  children?: CascaderOption[];
};

type CascaderValueExtend = {
  items: (CascaderOption | null)[];
  isLeaf: boolean;
};

interface AddressResItemType {
  id: string;
  name: string;
}

const GET_ADDRESS_ACTION: {
  [key: number]: any;
} = {
  0: redux.actions.asyncGetProvinces,
  1: redux.actions.asyncGetCities,
  2: redux.actions.asyncGetDistricts,
  3: redux.actions.asyncGetVillages,
};

const GET_ADDRESS_PARAMS: {
  [key: number]: (x: string) => any;
} = {
  0: (): { countryId: number } => {
    return {
      countryId: 1,
    };
  },
  1: (id: string): { provinceId: string } => {
    return {
      provinceId: id,
    };
  },
  2: (id: string): { cityId: string } => {
    return {
      cityId: id,
    };
  },
  3: (id: string): { districtId: string } => {
    return {
      districtId: id,
    };
  },
};

function AsyncLoadDataAddress(props: Props): JSX.Element {
  const [valueToOptions, setValueToOptions] = useState<{ [key: string]: CascaderOptionItem[] | null }>({
    0: props.addressList,
  });
  const [currentSelected, setCurrentSelected] = useState<(CascaderOption | null)[]>();
  const { visible, onClose, onSelectItem } = props;
  const ctx = useCtx();
  const t = useT();

  useEffect(() => {
    fetchAddress("", 0);
  }, []);

  useEffect(() => {
    // 代表用户已经选择到了乡镇，需要关闭弹窗
    if (currentSelected && currentSelected.length >= 4) {
      handleAddress();
    }
  }, [currentSelected]);

  const handleAddress = (): void => {
    if (currentSelected?.length) {
      const params = {
        provinceId: currentSelected[0]?.value,
        cityId: currentSelected[1]?.value,
        districtId: currentSelected[2]?.value,
        villageId: currentSelected[3]?.value,
        provinceName: currentSelected[0]?.label,
        cityName: currentSelected[1]?.label,
        districtName: currentSelected[2]?.label,
        villageName: currentSelected[3]?.label,
      };
      onClose?.();
      onSelectItem?.(params);
    }
  };

  const options = useMemo<any[]>(() => {
    function generate(v: string): any[] | undefined {
      const options = valueToOptions[v];
      if (options === null) {
        return undefined;
      }
      if (options === undefined) {
        return Cascader.optionSkeleton;
      }
      return options.map((option: CascaderOptionItem) => ({
        ...option,
        children: generate(option.value),
      }));
    }
    return generate("") ?? [];
  }, [valueToOptions]);

  async function fetchAddress(v: string, level: number): Promise<void> {
    if (v in valueToOptions) return;
    if (level > 3) {
      setValueToOptions((prev) => ({
        ...prev,
        [v]: null,
      }));
      return;
    }
    const params = GET_ADDRESS_PARAMS[level](v);
    const { success, data, errMsg } = await ctx.store.dispatch(GET_ADDRESS_ACTION[level](params));
    if (!success) {
      Toast.info({ content: errMsg || t("global: 网络错误,请稍后重试") });
      onClose?.();
      return;
    }
    // 没有数据，代表没有下一层级了，就要选中上一次选中的地址
    if (!data?.length) {
      handleAddress();
    }
    const options = data.map((item: AddressResItemType) => ({
      value: item.id,
      label: item.name,
    }));
    setValueToOptions((prev) => ({
      ...prev,
      [v]: options,
    }));
  }

  const handleSelect = (value: Array<string>, extend: CascaderValueExtend): void => {
    if (extend.items) {
      setCurrentSelected(extend.items);
    }
    value.forEach((v, index) => {
      fetchAddress(v, index + 1);
    });
  };

  return (
    <Cascader
      title={t("选择其他区域")}
      confirmText={<Iconfont name="close" className={styles.close_icon} onClick={onClose} />}
      cancelText={""}
      options={options}
      onSelect={handleSelect}
      visible={visible}
      onClose={onClose}
      className={styles.address_pop}
      value={[]}
    />
  );
}

export default AsyncLoadDataAddress;
